/**
 * Standalone Question Bank creation/edit panel (NA-197/198): the same
 * QuestionForm / useQuestionDraft primitives CreateQuestionModal uses,
 * rendered as a slide-in panel with no originating lesson block.
 *
 * Create mode (no `existingQuestion`): "Add to public question bank" defaults
 * checked for Likert only, matching the in-lesson modal (NA-288), and there
 * is no auto-select-into-block step on save — the caller just refreshes its
 * own list.
 *
 * Edit mode (`existingQuestion` present, NA-198): seeded from the existing
 * question via `dtoToBlockFields`; the public checkbox reflects the
 * question's current value; the footer replaces Cancel with a usage summary
 * plus a state-gated Archive-or-Delete action (each behind its own
 * confirmation dialog), while Save updates in place instead of creating.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faCheck, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import type {
  QuestionBlockType,
  QuestionDefinitionDto,
  QuestionTranslationFields,
  QuestionTranslationProjection,
  PublishJobProgress,
  PublishJobSummary,
  TranslationReplacementAction,
  LocaleOption,
} from '@course-authoring/shared-types';
import { useTheme } from '@course-authoring/shared-types';

import {
  QUESTION_SAVE_ERROR_MESSAGE,
  narrowQuestionApiIssues,
  useArchiveQuestionMutation,
  useCreateQuestionMutation,
  useDeleteQuestionMutation,
  useQuestionUsageQuery,
  usePatchQuestionTranslationMutation,
  useUnarchiveQuestionMutation,
  useUpdateQuestionMutation,
  useRetryPublishJobMutation,
  isPublishJobConflict,
  type QuestionApiIssue,
} from '../../api/questionApi';
import { EMPTY_RICH_TEXT_DOC } from '../interactionEditorUtils';
import { createBlockFromType } from '../../utils/blockFactory';
import {
  changedTranslationFields,
  dtoToBlockFields,
  initialTranslationFields,
  isQuestionBlock,
  prependCopyPrefix,
  questionBlockTypeLabel,
  translatableSource,
  type QuestionBlock,
} from './questionBlockConversion';
import { QuestionForm, useQuestionDraft } from './QuestionForm';
import { QuestionUsagePanel } from './QuestionUsagePanel';
import { QuestionTranslationForm } from './QuestionTranslationForm';
import {
  TranslationLanguageBanner,
  TranslationReplacementPrompt,
  TranslationStatusNote,
  type TranslationReplacementView,
} from '../TranslationStatusPrimitives';
import { useQuestionPreparation } from './useQuestionPreparation';
import { deriveCurrentPublishJobProgress, isActivePublishJob } from '@course-authoring/ui-blocks';
import { PublishJobStatus, PublishJobRunLog, PublishJobStatusWarning } from '@course-authoring/ui-blocks';

export interface QuestionCreatePanelProps {
  blockType: QuestionBlockType;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (dto: QuestionDefinitionDto & { publishJob?: PublishJobSummary | null }) => void;
  /** Accepted creation work carried into the saved question's editing session. */
  initialAcceptedJob?: PublishJobSummary | null;
  /** Edit mode (NA-198): when present, the panel opens seeded from this question instead of a blank draft. */
  existingQuestion?: QuestionDefinitionDto & {
    translation?: QuestionTranslationProjection;
    englishSource?: QuestionDefinitionDto;
  };
  /** Duplicate mode: seeds the draft from this question's fields (same as edit mode) but
   * stays in create mode — Save calls the create endpoint, producing a new, independent
   * question rather than updating this one. Mutually exclusive with `existingQuestion`. */
  duplicateFromQuestion?: QuestionDefinitionDto;
  selectedLocale?: string;
  selectedLanguageLabel?: string;
  languageOptions?: LocaleOption[];
  onJobStatusChange?: (questionId: number, job: PublishJobSummary | null) => void;
}

const INLINE_USAGE_LIMIT = 5;

function buildInitialBlock(blockType: QuestionBlockType, seedQuestion?: QuestionDefinitionDto, isDuplicate = false): QuestionBlock {
  const created = createBlockFromType(blockType);
  if (!isQuestionBlock(created)) {
    throw new Error(`Unexpected non-question block type: ${blockType}`);
  }
  if (seedQuestion) {
    const fields = dtoToBlockFields(seedQuestion);
    // Duplicate mode (NA-196) prepends "[COPY] " to the seeded question text;
    // edit mode must not — it's editing the same record in place.
    const description = isDuplicate ? prependCopyPrefix(fields.description ?? EMPTY_RICH_TEXT_DOC) : fields.description;
    return { ...created, ...fields, description } as QuestionBlock;
  }
  return {
    ...created,
    title: '',
    description: EMPTY_RICH_TEXT_DOC,
  };
}

function toDisplayError(error: unknown): { message: string; issues: QuestionApiIssue[] } {
  if (typeof error === 'object' && error !== null) {
    const record = error as { message?: unknown; issues?: unknown };
    const message = typeof record.message === 'string' ? record.message : QUESTION_SAVE_ERROR_MESSAGE;
    return { message, issues: narrowQuestionApiIssues(record.issues) };
  }
  return { message: QUESTION_SAVE_ERROR_MESSAGE, issues: [] };
}

export function QuestionCreatePanel({
  blockType,
  isOpen,
  onClose,
  onCreated,
  initialAcceptedJob,
  existingQuestion: incomingQuestion,
  duplicateFromQuestion,
  selectedLocale = 'en',
  selectedLanguageLabel = selectedLocale,
  languageOptions = [],
  onJobStatusChange,
}: QuestionCreatePanelProps): React.ReactElement {
  const { tokens } = useTheme();
  const [editorQuestion, setEditorQuestion] = useState(incomingQuestion);
  const existingQuestion = editorQuestion ?? incomingQuestion;
  const isEditMode = Boolean(existingQuestion);
  const isTranslationMode = isEditMode && selectedLocale !== 'en';
  const seedQuestion = existingQuestion ?? duplicateFromQuestion;
  // Editing an archived question (NA-177): it can no longer be added to new
  // lessons while archived, so the public-bank checkbox defaults unchecked
  // rather than mirroring the question's stored isPublic value, and the
  // footer drops its Archive/Delete action entirely — there's nothing to
  // archive again, and delete isn't offered here since the question may
  // still be referenced by lessons that used it before archiving.
  const isArchivedQuestion = isEditMode && Boolean(existingQuestion?.retiredAt);
  const createMutation = useCreateQuestionMutation();
  const updateMutation = useUpdateQuestionMutation();
  const translationMutation = usePatchQuestionTranslationMutation();
  const archiveMutation = useArchiveQuestionMutation();
  const deleteMutation = useDeleteQuestionMutation();
  const unarchiveMutation = useUnarchiveQuestionMutation();
  const retryMutation = useRetryPublishJobMutation();
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isRunLogOpen, setIsRunLogOpen] = useState(false);
  useEffect(() => { setIsRunLogOpen(false); }, [incomingQuestion?.questionDefinitionId, selectedLocale, isOpen]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUsageDialogOpen, setIsUsageDialogOpen] = useState(false);
  const [translationFields, setTranslationFields] = useState<QuestionTranslationFields>({});
  const [translationErrors, setTranslationErrors] = useState<string[]>([]);
  const [replacementAction, setReplacementAction] = useState<TranslationReplacementAction | undefined>();
  const [replacementView, setReplacementView] = useState<TranslationReplacementView>('prompt');

  const usageQuery = useQuestionUsageQuery(existingQuestion?.questionDefinitionId, { enabled: isOpen && isEditMode && !isTranslationMode });
  const usage = usageQuery.data ?? { count: 0, lessons: [] };
  const visibleUsageLessons = usage.lessons.slice(0, INLINE_USAGE_LIMIT);
  const hiddenUsageCount = Math.max(0, usage.count - INLINE_USAGE_LIMIT);
  // Locking removal for any usage at all is too coarse — a question referenced
  // only by Draft lessons isn't at risk of surprising a live audience. Lock
  // only once at least one referencing lesson has shipped (Published,
  // Published-editing — both raw `workflowState: 'published'` — or Archived).
  const hasNonDraftUsage = usage.lessons.some((lesson) => lesson.workflowState !== 'draft');
  // The lock only protects options/categories/items that are actually
  // persisted (and thus what the referencing lesson(s) depend on) — anything
  // the author adds locally in this draft, before Save, has no server id yet
  // and stays removable even while the lock is active.
  const lockedOptionIds = useMemo(
    () => new Set((existingQuestion?.answerOptions ?? []).map((option) => String(option.id))),
    [existingQuestion],
  );
  const lockedCategoryIds = useMemo(
    () => new Set((existingQuestion?.categories ?? []).map((category) => String(category.id))),
    [existingQuestion],
  );
  const lockedItemIds = useMemo(
    () => new Set((existingQuestion?.items ?? []).map((item) => String(item.id))),
    [existingQuestion],
  );
  const lockedMinOptionCount = existingQuestion ? (existingQuestion.answerOptions.length === 5 ? 5 : 7) : undefined;

  const isDuplicateMode = Boolean(duplicateFromQuestion);
  const initialBlock = useMemo(
    () => buildInitialBlock(blockType, seedQuestion, isDuplicateMode),
    [blockType, seedQuestion, isDuplicateMode],
  );
  // Standalone creation now mirrors the in-lesson modal: the public checkbox
  // defaults checked only for Likert (NA-288). Edit and duplicate modes
  // instead reflect the source question's current isPublic value (NA-198) —
  // except an archived question, which always defaults unchecked (NA-177).
  const initialIsPublic = isArchivedQuestion ? false : (seedQuestion?.isPublic ?? blockType === 'likert-scale');
  const form = useQuestionDraft({ initialBlock, initialIsPublic, scopeToActiveContext: true });
  const { reset: resetForm } = form;
  const baselineBlockRef = useRef(initialBlock);
  const baselineIsPublicRef = useRef(initialIsPublic);
  const baselineTranslationRef = useRef(existingQuestion ? initialTranslationFields(existingQuestion) : {});

  useEffect(() => {
    if (isOpen) {
      resetForm(initialBlock, initialIsPublic);
      baselineBlockRef.current = initialBlock;
      baselineIsPublicRef.current = initialIsPublic;
      baselineTranslationRef.current = existingQuestion ? initialTranslationFields(existingQuestion) : {};
      if (isTranslationMode && existingQuestion) {
        setTranslationFields(initialTranslationFields(existingQuestion));
        setTranslationErrors([]);
      }
      setReplacementAction(undefined);
      setReplacementView('prompt');
    }
  }, [isOpen, initialBlock, initialIsPublic, isTranslationMode, existingQuestion, resetForm]);

  // Edit mode only (NA-177): "Save changes" stays disabled until the author
  // actually changes something, so an unmodified form can't be resubmitted
  // for no reason. Plain JSON comparison is enough — the draft block and
  // isPublic are both plain, serializable data with no functions/Dates.
  const isEnglishDirty = !isEditMode
    || form.isPublic !== baselineIsPublicRef.current
    || JSON.stringify(form.block) !== JSON.stringify(baselineBlockRef.current);
  const isTranslationDirty = existingQuestion
    ? JSON.stringify(translationFields) !== JSON.stringify(baselineTranslationRef.current)
    : false;
  const isDirty = isTranslationMode ? isTranslationDirty : isEnglishDirty;
  const preparation = useQuestionPreparation(
    incomingQuestion?.questionDefinitionId,
    selectedLocale,
    isOpen,
    (detail) => {
      if (isDirty) return;
      const nextBlock = buildInitialBlock(blockType, detail);
      const nextIsPublic = detail.retiredAt ? false : detail.isPublic;
      baselineBlockRef.current = nextBlock;
      baselineIsPublicRef.current = nextIsPublic;
      baselineTranslationRef.current = initialTranslationFields(detail);
      resetForm(nextBlock, nextIsPublic);
      if (isTranslationMode) setTranslationFields(initialTranslationFields(detail));
      setEditorQuestion(detail);
    },
  );
  const observationScope = `${incomingQuestion?.questionDefinitionId ?? 'new'}:${selectedLocale}:${isOpen}`;
  const observedJobRef = useRef({ scope: observationScope, identity: initialAcceptedJob ? `${initialAcceptedJob.id}:${initialAcceptedJob.attempt}` : null as string | null });
  if (observedJobRef.current.scope !== observationScope) {
    observedJobRef.current = { scope: observationScope, identity: null };
  }
  if (isOpen && preparation.ready && isActivePublishJob(preparation.job) && preparation.job) {
    observedJobRef.current.identity = `${preparation.job.id}:${preparation.job.attempt}`;
  }
  const visibleJob = preparation.job?.status === 'completed'
    && (isDirty || observedJobRef.current.identity !== `${preparation.job.id}:${preparation.job.attempt}`)
    ? null : preparation.job;
  const scope = visibleJob?.scope as { kind?: unknown; locale?: unknown } | undefined;
  const preparationLocale = scope?.kind === 'translation' && typeof scope.locale === 'string' ? scope.locale : null;
  const preparationLanguage = languageOptions.find((option) => option.locale === preparationLocale)?.label ?? preparationLocale;
  const isMutationBlocked = isEditMode
    && (!preparation.ready || preparation.refreshing || isActivePublishJob(preparation.job));
  const jobId = preparation.job?.id;
  const jobStatus = preparation.job?.status;
  const jobStep = preparation.job?.currentStep;
  const jobAttempt = preparation.job?.attempt;
  const jobProgress: PublishJobProgress | null = preparation.job?.status === 'running'
    ? deriveCurrentPublishJobProgress(preparation.job)
    : null;
  const jobProgressCompleted = jobProgress?.completed;
  const jobProgressTotal = jobProgress?.total;
  useEffect(() => {
    if (!preparation.ready || !incomingQuestion || !onJobStatusChange) return;
    onJobStatusChange(incomingQuestion.questionDefinitionId, jobId === undefined ? null : {
      id: jobId,
      status: jobStatus ?? 'queued',
      currentStep: jobStep ?? null,
      attempt: jobAttempt ?? 1,
      ...(jobProgressCompleted !== undefined && jobProgressTotal !== undefined
        ? { progress: { completed: jobProgressCompleted, total: jobProgressTotal } }
        : {}),
    });
  }, [preparation.ready, incomingQuestion?.questionDefinitionId, onJobStatusChange, jobId, jobStatus, jobStep, jobAttempt, jobProgressCompleted, jobProgressTotal]);
  const hasTranslatableSourceChange = isEditMode
    && translatableSource(form.block, form.isPublic) !== translatableSource(baselineBlockRef.current, baselineIsPublicRef.current);
  const humanTranslationCount = existingQuestion?.translation?.summary.humanTranslationCount ?? 0;

  const typeLabel = questionBlockTypeLabel(blockType);
  const isSaving = createMutation.isPending || updateMutation.isPending || translationMutation.isPending;
  const isBusy = isSaving || archiveMutation.isPending || deleteMutation.isPending || unarchiveMutation.isPending || retryMutation.isPending;
  const isSaveDisabled = isBusy || isMutationBlocked || !isDirty;

  const handleMutationError = (error: unknown) => {
    if (isPublishJobConflict(error)) {
      if (error.publishJob) preparation.acceptJob(error.publishJob);
      else preparation.requireStatus();
      void preparation.poll();
    }
    return toDisplayError(error);
  };

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const observeAcceptedSave = (job: PublishJobSummary | null | undefined) => {
    observedJobRef.current.identity = job ? `${job.id}:${job.attempt}` : null;
    preparation.requireStatus();
    void preparation.poll();
  };

  const handleSave = async () => {
    if (isMutationBlocked || isBusy) return;
    if (isTranslationMode && existingQuestion) {
      const patch = changedTranslationFields(baselineTranslationRef.current, translationFields);
      if (Object.keys(patch).length === 0) {
        return;
      }
      try {
        const saved = await translationMutation.mutateAsync({
          questionDefinitionId: existingQuestion.questionDefinitionId,
          locale: selectedLocale,
          fields: patch,
        });
        // Cleared translations are omitted by the server and resolve from English.
        const source = existingQuestion.englishSource ?? existingQuestion;
        setEditorQuestion({
          ...existingQuestion,
          questionText: saved.fields.displayText ?? source.questionText,
          answerOptions: source.answerOptions.map((option) => ({
            ...option,
            label: saved.fields.options?.[String(option.id)] ?? option.label,
            correctAnswerExplanation: option.isTarget
              ? saved.fields.correctAnswerExplanation ?? option.correctAnswerExplanation
              : option.correctAnswerExplanation,
          })),
          categories: source.categories.map((category) => ({ ...category, label: saved.fields.categories?.[String(category.id)] ?? category.label })),
          items: source.items.map((item) => ({ ...item, label: saved.fields.items?.[String(item.id)] ?? item.label })),
          translation: saved,
        });
        setTranslationErrors([]);
        observeAcceptedSave(saved.publishJob);
      } catch (error) {
        setTranslationErrors([handleMutationError(error).message]);
      }
      return;
    }
    const draft = form.validate();
    if (!draft) return;
    try {
      if (isEditMode && existingQuestion) {
        const saved = await updateMutation.mutateAsync({
          questionDefinitionId: existingQuestion.questionDefinitionId,
          draft,
          replacementAction,
        });
        setEditorQuestion(saved);
        observeAcceptedSave(saved.publishJob);
      } else {
        const dto = await createMutation.mutateAsync({ draft });
        onCreated(dto);
      }
    } catch (error) {
      const { message, issues } = handleMutationError(error);
      const issueMessages = issues.map((issue) => issue.message);
      form.setErrors([...new Set([message, ...issueMessages])]);
    }
  };

  const handleConfirmArchive = async () => {
    if (!existingQuestion || isMutationBlocked) return;
    try {
      await archiveMutation.mutateAsync(existingQuestion.questionDefinitionId);
      setIsArchiveDialogOpen(false);
      onClose();
    } catch (error) {
      const { message } = handleMutationError(error);
      form.setErrors([message]);
      setIsArchiveDialogOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!existingQuestion || isMutationBlocked) return;
    try {
      await deleteMutation.mutateAsync(existingQuestion.questionDefinitionId);
      setIsDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      const { message } = handleMutationError(error);
      form.setErrors([message]);
      setIsDeleteDialogOpen(false);
    }
  };

  // Unarchive requires no confirmation (NA-196) — unlike Archive/Delete, the
  // ticket specifies no confirmation copy for it, and it's a low-consequence,
  // reversible action.
  const handleUnarchive = async () => {
    if (!existingQuestion || isMutationBlocked) return;
    try {
      await unarchiveMutation.mutateAsync(existingQuestion.questionDefinitionId);
      onClose();
    } catch (error) {
      const { message } = handleMutationError(error);
      form.setErrors([message]);
    }
  };

  const handleRetry = async () => {
    if (!preparation.job || preparation.job.status !== 'failed' || isMutationBlocked) return;
    if (isDirty) {
      if (isTranslationMode) setTranslationErrors(['Save your draft before retrying preparation.']);
      else form.setErrors(['Save your draft before retrying preparation.']);
      return;
    }
    try {
      const job = await retryMutation.mutateAsync(preparation.job.id);
      preparation.acceptJob(job);
    } catch (error) {
      const { message } = handleMutationError(error);
      if (isTranslationMode) setTranslationErrors([message]);
      else form.setErrors([message]);
    }
  };

  const styles = useMemo((): Record<string, CSSProperties> => ({
    overlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'flex-end',
      pointerEvents: isOpen ? 'auto' : 'none',
    },
    scrim: {
      position: 'absolute',
      inset: 0,
      backgroundColor: `color-mix(in srgb, ${tokens.backgrounds.inverse} 40%, transparent)`,
      opacity: isOpen ? 1 : 0,
      transition: tokens.ui.transitionDefault,
    },
    panel: {
      position: 'relative',
      width: '600px',
      maxWidth: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: tokens.backgrounds.page,
      borderLeft: `1px solid ${tokens.borders.default}`,
      // Directional (left-only) shadow for a right-edge slide-in panel — none
      // of the design system's shadow tokens are directional, so this is a
      // deliberate one-off match to the Question Bank prototype's panel.
      boxShadow: `-4px 0 20px rgba(0, 0, 0, 0.10)`,
      transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
      transition: 'transform 0.2s ease',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      borderBottom: `1px solid ${tokens.borders.default}`,
    },
    title: {
      margin: 0,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '20px',
      fontWeight: 700,
      color: tokens.text.primary,
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      fontSize: '22px',
      lineHeight: 1,
      padding: 0,
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      overflowY: 'auto',
      flex: 1,
    },
    footer: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      borderTop: `1px solid ${tokens.borders.default}`,
    },
    cancelButton: {
      padding: `${tokens.spacing.sm} ${tokens.spacing.lg}`,
      backgroundColor: 'transparent',
      color: tokens.text.muted,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    // Matches ImageLibraryPage's "Save to library" button (fullWidthButton):
    // solid brand blue with a plain opacity fade for the disabled state,
    // rather than a separate flat color.
    saveButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 96,
      gap: '6px',
      padding: '10px 16px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    // The edit panel's unsaved-changes gate — "Save changes" fades to this
    // opacity (matching ImageLibraryPage's disabled treatment) until the
    // author actually changes something, rather than switching to a
    // different flat color.
    saveButtonInactive: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    usageNotice: {
      padding: '10px',
      borderRadius: '6px',
      background: tokens.backgrounds.lessonSectionTinted,
      color: tokens.text.primary,
      fontSize: '13px',
    },
    usageList: { margin: '8px 0 0 0', paddingLeft: '18px' },
    usageListItem: { marginTop: '4px', color: tokens.text.muted },
    usageLink: { color: tokens.text.muted, textDecoration: 'none' },
    showMoreButton: {
      marginTop: '8px',
      padding: 0,
      border: 'none',
      background: 'transparent',
      color: tokens.ui.selectionBorder,
      cursor: 'pointer',
      font: 'inherit',
      fontWeight: 600,
    },
    editFooter: {
      flexWrap: 'wrap',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: tokens.spacing.md,
      padding: tokens.spacing.lg,
      borderTop: `1px solid ${tokens.borders.default}`,
    },
    usageSummaryGroup: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacing.md },
    usageSummary: { color: tokens.text.muted, fontSize: '13px' },
    archiveButton: {
      padding: '10px 16px',
      backgroundColor: 'transparent',
      color: tokens.text.primary,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    deleteButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '10px 16px',
      backgroundColor: tokens.backgrounds.page,
      color: tokens.ui.errorText,
      border: `1px solid ${tokens.ui.errorBorder}`,
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    // Filled variant of archiveButton for the "Archive question?" confirmation
    // dialog's confirm action — distinct from the outline archiveButton used
    // to open that dialog from the footer.
    archiveConfirmButton: {
      padding: '10px 16px',
      backgroundColor: tokens.text.primary,
      color: tokens.text.onBrand,
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    dialogOverlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: `color-mix(in srgb, ${tokens.backgrounds.inverse} 50%, transparent)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1100,
      padding: '24px',
    },
    // Border/shadow are deliberate one-off literal values (not design tokens)
    // matching the confirm-dialog mockup exactly — no token matches these.
    dialog: {
      width: '360px',
      maxWidth: '100%',
      background: tokens.backgrounds.page,
      border: '1px solid #ddd',
      borderRadius: '8px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, .18)',
      padding: '28px 32px',
    },
    dialogTitle: { margin: '0 0 8px 0', color: tokens.text.primary, fontSize: '18px' },
    dialogBody: { margin: 0, color: tokens.text.muted, fontSize: '15px', lineHeight: 1.5 },
    dialogFooter: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' },
    secondaryButton: {
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      padding: '10px 16px',
      background: tokens.backgrounds.page,
      color: tokens.text.primary,
      cursor: 'pointer',
      fontWeight: 600,
    },
  }), [tokens, isOpen]);

  const preparationStatus = isEditMode ? (
    <PublishJobStatus
      ready={preparation.ready}
      job={visibleJob}
      languageOptions={languageOptions}
      isRunLogOpen={isRunLogOpen}
      onToggleRunLog={() => setIsRunLogOpen((open) => !open)}
      onRetry={() => { void handleRetry(); }}
      isRetrying={retryMutation.isPending}
      isMutationBlocked={isMutationBlocked}
      isDirty={isDirty}
      isSaving={isSaving}
    />
  ) : null;
  const saveActionsStyle: CSSProperties = { marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: tokens.spacing.sm, flexWrap: 'nowrap', flex: '1 1 max-content', minWidth: 0 };

  return (
    <div style={styles.overlay} aria-hidden={!isOpen}>
      <div style={styles.scrim} />
      <aside
        style={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="question-create-panel-title"
      >
        <div style={styles.header}>
          <h2 id="question-create-panel-title" style={styles.title}>
            {isTranslationMode ? 'Translate' : isEditMode ? 'Edit' : 'New'} {typeLabel}
          </h2>
          <button
            type="button"
            style={styles.closeButton}
            onClick={handleClose}
            aria-label="Close panel"
            disabled={isBusy}
          >
            ×
          </button>
        </div>

        {/* Outside the scrollable body so the language context stays visible while
            the author scrolls a long translation form. */}
        {isTranslationMode && (
          <TranslationLanguageBanner language={selectedLanguageLabel} flush />
        )}

        <div style={styles.body}>
          {isEditMode && isActivePublishJob(preparation.job) && <div role="status" style={{ display: 'flex', alignItems: 'flex-start', gap: tokens.spacing.sm, padding: tokens.spacing.sm, border: `1px solid ${tokens.borders.default}`, borderRadius: tokens.radius.md, background: tokens.backgrounds.page, color: tokens.text.muted, fontSize: 13, lineHeight: 1.4 }}><FontAwesomeIcon icon={faCircleInfo} aria-hidden="true" style={{ color: tokens.ui.fixedAccent, marginTop: 2, flexShrink: 0 }} /><span>Editing is paused while preparation runs. You can close this panel while the work continues.</span></div>}
          {/* The replacement decision leads the editor: it asks a question the
              author must answer before saving, so it cannot sit below a form long
              enough to scroll it out of view. */}
          {!isTranslationMode && isEditMode && hasTranslatableSourceChange && humanTranslationCount > 0 && (
            <TranslationReplacementPrompt
              humanTranslationCount={humanTranslationCount}
              view={replacementView}
              disabled={isBusy || isMutationBlocked}
              onQueue={() => { setReplacementAction('queue'); setReplacementView('queued'); }}
              onPreserve={() => { setReplacementAction('preserve'); setReplacementView('preserved'); }}
              onChange={() => { setReplacementAction(undefined); setReplacementView('prompt'); }}
            />
          )}

          {isTranslationMode && existingQuestion ? (
            <div inert={isMutationBlocked} aria-disabled={isMutationBlocked}>
              {existingQuestion.translation && (
                <TranslationStatusNote status={existingQuestion.translation.status} />
              )}
              <QuestionTranslationForm
                englishSource={existingQuestion.englishSource ?? existingQuestion}
                fields={translationFields}
                onChange={setTranslationFields}
                errors={translationErrors}
              />
            </div>
          ) : <div inert={isMutationBlocked} aria-disabled={isMutationBlocked}><QuestionForm
            block={form.block}
            onFieldChange={form.setField}
            isPublic={form.isPublic}
            onIsPublicChange={form.setIsPublic}
            errors={form.errors}
            autoFocus={isOpen}
            scopeToActiveContext
            disableOptionRemoval={isEditMode && usage.count > 0 && hasNonDraftUsage}
            lockedOptionIds={lockedOptionIds}
            lockedCategoryIds={lockedCategoryIds}
            lockedItemIds={lockedItemIds}
            lockedMinOptionCount={lockedMinOptionCount}
          /></div>}

          {!isTranslationMode && isEditMode && usage.count > 0 && (
            <div style={styles.usageNotice}>
              Used in {usage.count} {usage.count === 1 ? 'lesson' : 'lessons'}.
              {visibleUsageLessons.length > 0 && (
                <ul style={styles.usageList}>
                  {visibleUsageLessons.map((lesson) => (
                    <li key={lesson.lessonId} style={styles.usageListItem}>
                      <Link to={`/lessons/${lesson.lessonId}`} style={styles.usageLink}>
                        {lesson.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {hiddenUsageCount > 0 && (
                <button
                  type="button"
                  style={styles.showMoreButton}
                  onClick={() => setIsUsageDialogOpen(true)}
                >
                  Show {hiddenUsageCount} more
                </button>
              )}
            </div>
          )}
        </div>

        {isEditMode && isRunLogOpen && visibleJob && <PublishJobRunLog job={visibleJob} languageOptions={languageOptions}
          steps={preparationLocale ? ['narration'] : ['translation', 'narration']}
          copy={{ preparing: preparationLocale ? `Preparing audio narration for ${preparationLanguage}` : 'Preparing translations and audio',
            ready: preparationLocale ? `Audio narration is ready for ${preparationLanguage}` : 'Translations and audio are ready',
            failureDescription: 'Preparation stopped. Saved content remains editable; successful outputs from this attempt may already be available. Retry preparation starts the workflow again.' }} onClose={() => setIsRunLogOpen(false)} />}

        {preparation.warning && isEditMode && <div style={{ margin: `0 ${tokens.spacing.lg} ${tokens.spacing.sm}` }}><PublishJobStatusWarning message="Couldn't refresh preparation status. Showing the last known state; it will update once the connection recovers. Your unsaved edits are kept." /></div>}

        {isTranslationMode ? (
          <div style={styles.footer}>
            <button type="button" style={styles.cancelButton} onClick={handleClose} disabled={isSaving}>Close</button>
            <div style={saveActionsStyle}>
              {preparationStatus}
              <button
                type="button"
                style={{ ...styles.saveButton, flexShrink: 0, whiteSpace: 'nowrap', ...(!isDirty || isSaving || isMutationBlocked ? styles.buttonDisabled : {}) }}
                onClick={() => { void handleSave(); }}
                disabled={!isDirty || isSaving || isMutationBlocked}
                aria-busy={isSaving}
              >
                Save
              </button>
            </div>
          </div>
        ) : isEditMode ? (
          <div style={styles.editFooter}>
            <div style={styles.usageSummaryGroup}>
              <span style={styles.usageSummary}>
                {usage.count > 0 ? `Used in ${usage.count} ${usage.count === 1 ? 'lesson' : 'lessons'}` : 'Not used in any lessons'}
              </span>
              {isArchivedQuestion ? (
                <button
                  type="button"
                  style={{ ...styles.archiveButton, ...(isBusy || isMutationBlocked ? styles.buttonDisabled : {}) }}
                  onClick={() => { void handleUnarchive(); }}
                  disabled={isBusy || isMutationBlocked}
                >
                  {unarchiveMutation.isPending ? 'Unarchiving…' : 'Unarchive'}
                </button>
              ) : (usage.count > 0 ? (
                <button
                  type="button"
                  style={{ ...styles.archiveButton, ...(isBusy || isMutationBlocked ? styles.buttonDisabled : {}) }}
                  onClick={() => setIsArchiveDialogOpen(true)}
                  disabled={isBusy || isMutationBlocked}
                >
                  Archive
                </button>
              ) : (
                <button
                  type="button"
                  style={{ ...styles.deleteButton, ...(isBusy || isMutationBlocked ? styles.buttonDisabled : {}) }}
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isBusy || isMutationBlocked}
                >
                  <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 14 }} />
                  Delete
                </button>
              ))}
            </div>
            <div style={saveActionsStyle}>
              {preparationStatus}
              <button
                type="button"
                style={{
                  ...styles.saveButton,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  ...(isBusy || isMutationBlocked ? styles.buttonDisabled : {}),
                  ...(!isBusy && !isDirty ? styles.saveButtonInactive : {}),
                }}
                onClick={() => { void handleSave(); }}
                disabled={isSaveDisabled}
                aria-busy={isSaving}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.footer}>
            <button
              type="button"
              style={{ ...styles.cancelButton, ...(isSaving ? styles.buttonDisabled : {}) }}
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{ ...styles.saveButton, ...(isSaving ? styles.buttonDisabled : {}) }}
              onClick={() => { void handleSave(); }}
              disabled={isSaving}
              aria-busy={isSaving}
            >
              {isSaving ? 'Saving…' : (
                <>
                  <FontAwesomeIcon icon={faCheck} style={{ fontSize: 14 }} />
                  Save
                </>
              )}
            </button>
          </div>
        )}
      </aside>

      {isEditMode && isArchiveDialogOpen && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-question-dialog-title"
            aria-describedby="archive-question-dialog-description"
          >
            <h2 id="archive-question-dialog-title" style={styles.dialogTitle}>Archive question?</h2>
            <p id="archive-question-dialog-description" style={styles.dialogBody}>
              This question will no longer be available to add to new lessons. Existing lessons that use it won't be affected.
            </p>
            <div style={styles.dialogFooter}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => setIsArchiveDialogOpen(false)}
                disabled={archiveMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...styles.archiveConfirmButton, ...(archiveMutation.isPending ? styles.buttonDisabled : {}) }}
                onClick={() => { void handleConfirmArchive(); }}
                disabled={archiveMutation.isPending || isMutationBlocked}
              >
                {archiveMutation.isPending ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditMode && isDeleteDialogOpen && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-question-dialog-title"
            aria-describedby="delete-question-dialog-description"
          >
            <h2 id="delete-question-dialog-title" style={styles.dialogTitle}>Delete question?</h2>
            <p id="delete-question-dialog-description" style={styles.dialogBody}>This cannot be undone.</p>
            <div style={styles.dialogFooter}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...styles.deleteButton, ...(deleteMutation.isPending ? styles.buttonDisabled : {}) }}
                onClick={() => { void handleConfirmDelete(); }}
                disabled={deleteMutation.isPending || isMutationBlocked}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditMode && (
        <QuestionUsagePanel
          isOpen={isUsageDialogOpen}
          onClose={() => setIsUsageDialogOpen(false)}
          questionText={existingQuestion?.questionTextPlain ?? ''}
          usage={usage}
        />
      )}
    </div>
  );
}

QuestionCreatePanel.displayName = 'QuestionCreatePanel';
